import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { XIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router';
import * as yup from 'yup';

import { QuickLanguageOption } from '@/components/atoms/QuickLanguageOption/QuickLanguageOption';
import {
  SidebarWrapper,
  SidebarWrapperContent,
  SidebarWrapperFooter,
  SidebarWrapperForm,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { UnsavedChangesModal } from '@/components/organisms/Modals/UnsavedChangesModal/UnsavedChangesModal';
import { Button } from '@/components/ui/button';
import { PopoverCombobox } from '@/components/ui/custom/PopoverCombobox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import useCollections from '@/hooks/useCollections';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { setCollectionId } from '@/redux/session/actions';
import { selectAccountSlug, selectCollectionId } from '@/redux/session/selectors';
import { setSidebarContent, setSidebarMeta } from '@/redux/sidebar/actions';
import { selectSidebar } from '@/redux/sidebar/selectors';
import { languageByCountryCode } from '@/shared/languageCodes';
import { BrainLanguage } from '@/shared/types/brain';
import { SidebarOptions } from '@/shared/types/sidebar';
import { COLLECTION_LANGUAGES } from '@/util/constants';
import { formatLanguageLabel, prioritizeLanguageOptions } from '@/util/languageOptions';
import { hasDirtyFields, useHasDirtyFields } from '@/util/util';
import { createCollectionSchema } from '@/util/validation/schema-builders';

type CollectionFormValues = yup.InferType<ReturnType<typeof createCollectionSchema>>;

// Configuration based on mode
type ConfigType = {
  formTitle: string;
  submitLabel: string;
  editableFields: Array<keyof CollectionFormValues>;
};

export const CollectionFormSkeleton = () => {
  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-8 w-8 rounded-full" />
      </SidebarWrapperTitle>

      <div className="p-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-4 w-48" />

            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-8 w-8 rounded-full" />
            </div>
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Skeleton className="h-4 w-4 rounded-full" />
                <Skeleton className="h-4 w-20" />
              </div>
              <div className="flex items-center space-x-2">
                <Skeleton className="h-4 w-4 rounded-full" />
                <Skeleton className="h-4 w-24" />
              </div>
              <div className="flex items-center space-x-2">
                <Skeleton className="h-4 w-4 rounded-full" />
                <Skeleton className="h-4 w-20" />
              </div>
              <div className="flex items-center space-x-2">
                <Skeleton className="h-4 w-4 rounded-full" />
                <Skeleton className="h-4 w-20" />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-row-reverse items-center gap-2">
          <Skeleton className="h-9 w-20" />
          <Skeleton className="h-9 w-20" />
        </div>
      </div>
    </SidebarWrapper>
  );
};

type StartWithOption = 'faq' | 'documents' | 'webpages' | 'external';

/**
 * Maps start_with option to corresponding sidebar option
 */
const getStartWithSidebarOption = (startWith: StartWithOption): SidebarOptions => {
  const sidebarMapping: Record<StartWithOption, SidebarOptions> = {
    faq: 'add_faq',
    documents: 'add_txt_document',
    webpages: 'add_webpages',
    external: 'add_external_sources',
  };
  return sidebarMapping[startWith];
};

export const CollectionForm = () => {
  const collectionIdFromRedux = useSelector(selectCollectionId);
  const sidebar = useSelector(selectSidebar);
  const { collectionId } = useParams();
  const finalCollectionId = collectionId || collectionIdFromRedux;
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);

  const mode = finalCollectionId ? 'rename' : 'create';
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { collection, createCollection, updateCollection, getStatus } =
    useCollections(finalCollectionId);

  const config: ConfigType =
    mode === 'create'
      ? {
          formTitle: t('collections.create_collection'),
          submitLabel: t('common.create'),
          editableFields: ['name', 'language', 'start_with'],
        }
      : {
          formTitle: t('collections.rename_collection'),
          submitLabel: t('common.rename'),
          editableFields: ['name'],
        };

  const options: { value: StartWithOption; label: string }[] = [
    { value: 'faq', label: t('collections.tabs.faq') },
    { value: 'documents', label: t('collections.tabs.documents') },
    { value: 'webpages', label: t('collections.tabs.webpages') },
    { value: 'external', label: t('collections.tabs.external') },
  ];

  // Capture defaultName before clearing meta to avoid stale data on future opens
  const defaultName = sidebar.meta?.defaultName;

  useEffect(() => {
    if (defaultName) {
      dispatch(setSidebarMeta(undefined));
    }
  }, [defaultName, dispatch]);

  // Set up form with react-hook-form and yup resolver
  const form = useForm<CollectionFormValues>({
    resolver: yupResolver<CollectionFormValues, unknown, CollectionFormValues>(
      createCollectionSchema(!!finalCollectionId, collection?.name)
    ),
    mode: 'onChange',
    defaultValues: {
      name: collection?.name || defaultName || '',
      language: collection?.language || '',
      start_with: 'faq',
    },
  });
  const isFieldEditable = (field: keyof CollectionFormValues) =>
    config.editableFields.includes(field);

  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);

  // Patch createCollection to add navigation on success
  const createCollectionWithNav = (values: CollectionFormValues) => {
    createCollection(values, {
      onSuccess: (resp) => {
        const targetSidebar = getStartWithSidebarOption(values.start_with as StartWithOption);
        dispatch(setSidebarContent(null)); // Close current sidebar
        if (slug && resp.collection_id) {
          // We need to wait for the sidebar to close before navigating
          // Otherwise the Unsaved changes modal will show up
          setTimeout(() => {
            // Pass the target sidebar as a URL parameter
            navigate(
              `/${slug}/knowledge-bases/${resp.collection_id}/category/${values.start_with}?sidebar=${targetSidebar}`
            );
          }, 100);
        }
      },
      onError: () => {
        dispatch(setSidebarContent(null));
      },
    });
  };

  const onSubmit = async (values: CollectionFormValues) => {
    if (mode === 'create') {
      createCollectionWithNav(values);
    } else if (mode === 'rename' && collection) {
      updateCollection({ collection_id: finalCollectionId, ...values });
      dispatch(setSidebarContent(null));
    }

    dispatch(setCollectionId(null));
  };

  const languageOptions = prioritizeLanguageOptions(
    Object.keys(languageByCountryCode)
      .filter((lang) => COLLECTION_LANGUAGES.includes(lang))
      .map((i) => ({ value: i.toLowerCase(), label: formatLanguageLabel(i) }))
  );

  useEffect(() => {
    // Set the form values to the collection values if they exist
    // This happens when user is editing a collection
    if (collection?.name) {
      form.reset({ name: collection.name, language: collection.language ?? '' });

      const timer = setTimeout(() => {
        form.setFocus('name', { shouldSelect: true });
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [collection, form]);

  const handleClose = () => {
    const hasUnsavedChanges = hasDirtyFields(form);

    if (hasUnsavedChanges) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      dispatch(setSidebarContent(null));
    }

    dispatch(setCollectionId(null));
  };

  const isDirty = useHasDirtyFields(form.control);
  useTrackFormState({ isDirty, formId: 'collection-form' });
  // Close the sidebar when the Escape key is pressed
  useEscapeKey(handleClose);

  if (collectionId && getStatus === 'pending') {
    return <CollectionFormSkeleton />;
  }

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="text-base leading-5 font-medium">{config.formTitle}</h2>
        <Button variant="ghost-neutral" size="icon-sm" type="button" onClick={handleClose}>
          <XIcon size={16} className="text-foreground-muted" />
        </Button>
      </SidebarWrapperTitle>

      <Form {...form}>
        <SidebarWrapperForm onSubmit={form.handleSubmit(onSubmit)}>
          <SidebarWrapperContent className="px-1 pb-1">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('collections.form.collection_title')}</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={!isFieldEditable('name')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isFieldEditable('language') && (
              <FormField
                control={form.control}
                name="language"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('field.language')}</FormLabel>

                    <PopoverCombobox
                      options={languageOptions}
                      value={field.value}
                      onChange={field.onChange}
                      triggerPlaceholder={t('common.select_an_option')}
                    />
                    <FormDescription>{t('collections.form.language_helper')}</FormDescription>
                    <FormMessage />

                    <div className="flex flex-wrap items-center gap-2">
                      <QuickLanguageOption
                        data-language={BrainLanguage.ENGLISH}
                        handleClick={() => {
                          field.onChange('en');
                        }}
                        language="en"
                        isActive={field.value === 'en'}
                      />
                      <QuickLanguageOption
                        data-language={BrainLanguage.PORTUGUESE_BRAZILIAN}
                        handleClick={() => {
                          field.onChange('pt-br');
                        }}
                        language="pt-br"
                        isActive={field.value === 'pt-br'}
                      />
                      <QuickLanguageOption
                        data-language={BrainLanguage.SPANISH}
                        handleClick={() => {
                          field.onChange('es');
                        }}
                        language="es"
                        isActive={field.value === 'es'}
                      />
                      <QuickLanguageOption
                        data-language={BrainLanguage.GREEK}
                        handleClick={() => {
                          field.onChange('el');
                        }}
                        language="el"
                        isActive={field.value === 'el'}
                      />
                    </div>
                  </FormItem>
                )}
              />
            )}

            {isFieldEditable('start_with') && (
              <FormField
                control={form.control}
                name="start_with"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('collections.form.start_with_title')}</FormLabel>
                    <RadioGroup
                      value={field.value}
                      onValueChange={(value) => field.onChange(value as StartWithOption)}
                    >
                      {options.map((option) => (
                        <div key={option.value} className="flex items-center space-x-2">
                          <RadioGroupItem value={option.value} id={option.value} />
                          <Label
                            htmlFor={option.value}
                            className="cursor-pointer text-sm font-normal"
                          >
                            {option.label}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </FormItem>
                )}
              />
            )}
          </SidebarWrapperContent>

          <SidebarWrapperFooter>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {config.submitLabel}
            </Button>

            <Button type="button" variant="ghost-neutral" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
          </SidebarWrapperFooter>
        </SidebarWrapperForm>
      </Form>

      {isUnsavedChangesModalOpen && (
        <UnsavedChangesModal
          onDiscard={() => {
            setIsUnsavedChangesModalOpen(false);
            dispatch(setSidebarContent(null));
          }}
          onGoBack={() => {
            setIsUnsavedChangesModalOpen(false);
          }}
          onOpenChange={setIsUnsavedChangesModalOpen}
        />
      )}
    </SidebarWrapper>
  );
};
